"use client";

import React from "react";
import { motion } from "framer-motion";
import { Building2, House as Home, ShieldCheck, Hammer, ArrowRight, CircleCheck as CheckCircle2, Umbrella, ShieldAlert, HardHat, ChevronRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
const residentialFeatures = ["Roof repairs & structural alterations", "House extensions & loft conversions", "Chimney repairs & repointing access", "External wall rendering & painting", "Strict perimeter care for neighbouring properties"];
const commercialFeatures = ["Town-centre commercial & retail premises", "Multi-storey office developments", "High-density commercial refurbishment", "Occupied sites with pedestrian protection gantries", "Full NASC & CHAS compliance"];
const tradeAccessServices = [{
  title: "Roofing Scaffolding",
  description: "Safe working platforms and hoist access tailored for roofers.",
  to: "/RoofingScaffoldingPage",
  icon: Hammer
}, {
  title: "Temporary Roofs",
  description: "Full weather protection systems keeping open structures dry.",
  to: "/TemporaryRoofsPage",
  icon: Umbrella
}, {
  title: "Edge Protection",
  description: "Handrails, toe boards, and guardrail barriers for site safety.",
  to: "/EdgeProtectionPage",
  icon: ShieldAlert
}];
export default function WandsworthScaffolding() {
  return <section id="wandsworthscaffoldingservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <Badge variant="outline" className="mb-4">
            <Building2 className="size-3 text-primary" />
            Wandsworth & South West London
          </Badge>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground font-default">
            Scaffolding services across Wandsworth
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">
            From town-centre commercial properties and busy office developments to
            residential streets and roof repairs, we design and erect compliant
            access solutions for every Wandsworth project.
          </p>
        </div>

        {/* Two Main Subsections: Residential & Commercial */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
          {/* Residential Scaffolding Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          ease: "easeOut"
        }}>
            <Card className="h-full border border-border shadow-sm flex flex-col justify-between hover:border-primary/40 transition-[border-color,box-shadow]">
              <div>
                <CardHeader>
                  <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                    <Home className="size-6" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-card-foreground">
                    Residential scaffolding in Wandsworth
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground mt-2">
                    Carefully planned structures for private homeowners, builders, and
                    residential contractors working in tight urban streets.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {residentialFeatures.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-3 text-sm text-card-foreground/90">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </div>

              <CardFooter className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-6 border-t border-border">
                <Button asChild variant="default" className="w-full sm:w-auto">
                  <Link to="/ResidentialScaffoldingPage">
                    Residential Scaffolding
                    <ChevronRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <Link to="/ChimneyScaffoldingPage">
                    Chimney Scaffolding
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>

          {/* Commercial Scaffolding Card */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: "easeOut"
        }}>
            <Card className="h-full border border-border shadow-sm flex flex-col justify-between hover:border-primary/40 transition-[border-color,box-shadow]">
              <div>
                <CardHeader>
                  <div className="size-12 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                    <HardHat className="size-6" />
                  </div>
                  <CardTitle className="text-2xl font-bold text-card-foreground">
                    Commercial scaffolding in Wandsworth
                  </CardTitle>
                  <CardDescription className="text-base text-muted-foreground mt-2">
                    Engineered scaffolding for main contractors, commercial
                    refurbishments, and occupied properties needing strict safety
                    protocols.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3">
                    {commercialFeatures.map((item, i) => <li key={item} data-index={i} className="flex items-start gap-3 text-sm text-card-foreground/90">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </li>)}
                  </ul>
                </CardContent>
              </div>

              <CardFooter className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-6 border-t border-border">
                <Button asChild variant="default" className="w-full sm:w-auto">
                  <Link to="/CommercialScaffoldingPage">
                    Commercial Scaffolding
                    <ChevronRight className="size-4" />
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full sm:w-auto">
                  <Link to="/MainContractorsDevelopersPage">
                    Main Contractors & Developers
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </CardFooter>
            </Card>
          </motion.div>
        </div>

        {/* Roofing & Trade Access Block */}
        <div className="mt-12 bg-muted/40 rounded-2xl p-6 sm:p-8 lg:p-10 border border-border">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-8">
            <div>
              <div className="inline-flex items-center gap-2 text-sm font-semibold text-primary mb-2">
                <ShieldCheck className="size-4" />
                Specialist Trade Support
              </div>
              <h3 className="text-2xl font-bold text-foreground font-default">
                Roofing & trade access
              </h3>
              <p className="text-sm sm:text-base text-muted-foreground mt-1 max-w-2xl">
                Dedicated solutions designed around trade requirements, complete weather
                protection, and strict safety regulations on active sites.
              </p>
            </div>
            <Button asChild variant="secondary" size="sm" className="self-start md:self-auto">
              <Link to="/ContactRequestQuotePage">
                Request site survey
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>

          <Separator className="mb-8" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {tradeAccessServices.map((service, i) => {
            const IconComp = service.icon;
            return <div key={service.title} data-index={i} className="bg-card rounded-xl p-6 border border-border shadow-xs flex flex-col justify-between hover:border-primary/30 transition-[border-color,box-shadow]">
                  <div>
                    <div className="size-10 rounded-md bg-accent text-accent-foreground flex items-center justify-center mb-4">
                      <IconComp className="size-5 text-primary" />
                    </div>
                    <h4 className="font-semibold text-lg text-card-foreground">
                      {service.title}
                    </h4>
                    <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                      {service.description}
                    </p>
                  </div>
                  <div className="mt-6 pt-4 border-t border-border">
                    <Button asChild variant="link" className="p-0 h-auto font-medium text-primary hover:text-primary/80 inline-flex items-center gap-1.5">
                      <Link to={service.to}>
                        View details
                        <ChevronRight className="size-3.5" />
                      </Link>
                    </Button>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}